"use client";

import { create } from "zustand";

import type { PatientSummary } from "@/types/patient";

export type DeskPhoneCallDirection = "INBOUND" | "OUTBOUND";

/**
 * Safety net for a pop whose server-side dismiss never arrives — the socket
 * was down when it fired, or the call ended by a path that doesn't finalize.
 * The dismiss is the real mechanism; this only bounds the damage.
 *
 * Must sit clear of the longest call a clinic actually makes, because
 * `receivedAt` is stamped at ring time and never refreshed: the answer-time
 * pop is swallowed as a duplicate, and it only targets the answering phone's
 * recipients anyway, so it could never serve as a keep-alive for everyone
 * else. Expiring early would drop patient context mid-conversation.
 *
 * Sized off real traffic — 650 answered calls over 30 days at the clinic that
 * reported this: mean 1.5 min, p99 7.4 min, longest 24.5 min, none over 30.
 * An hour is ~2.4x the observed maximum, so a real call effectively cannot
 * outlive it, while a genuinely stranded pop still clears well inside a shift.
 */
export const POP_TTL_MS = 60 * 60 * 1000;

/** Matches the cadence of `access-expiry-store`. */
const TICK_INTERVAL_MS = 30_000;

export interface DeskPhoneScreenPop {
  callId: string;
  /** Null at ring time — several handsets ring at once. Debugging only. */
  deskPhoneId?: string | null;
  direction: DeskPhoneCallDirection;
  patient: PatientSummary;
  chatId?: string;
  receivedAt: number;
}

interface DeskPhoneScreenPopStore {
  /** Active pops keyed by callId. Webhook retries dedupe naturally. */
  pops: Record<string, DeskPhoneScreenPop>;
  add: (pop: Omit<DeskPhoneScreenPop, "receivedAt">) => void;
  /** Remove a single pop (server-driven dismiss or local user dismiss). */
  remove: (callId: string) => void;
  clear: () => void;
  /** Drop pops older than POP_TTL_MS. Driven by the ticker below. */
  sweepExpired: () => void;
}

// Module-level so the interval is tied to the data, not to any component's
// lifetime: a pop can outlive the subtree that happened to be mounted when it
// arrived. Started on the first pop, stopped the moment none remain, so an
// idle console holds no timer.
let tickHandle: ReturnType<typeof setInterval> | null = null;

function startTicker(sweep: () => void) {
  if (tickHandle !== null) return;
  tickHandle = setInterval(sweep, TICK_INTERVAL_MS);
}

function stopTicker() {
  if (tickHandle === null) return;
  clearInterval(tickHandle);
  tickHandle = null;
}

export const useDeskPhoneScreenPopStore = create<DeskPhoneScreenPopStore>(
  (set, get) => ({
    pops: {},
    add: (pop) => {
      set((state) => {
        if (state.pops[pop.callId]) return state;
        return {
          pops: {
            ...state.pops,
            [pop.callId]: { ...pop, receivedAt: Date.now() },
          },
        };
      });
      if (Object.keys(get().pops).length > 0) {
        startTicker(() => get().sweepExpired());
      }
    },
    remove: (callId) => {
      set((state) => {
        if (!state.pops[callId]) return state;
        const next = { ...state.pops };
        delete next[callId];
        return { pops: next };
      });
      if (Object.keys(get().pops).length === 0) stopTicker();
    },
    clear: () => {
      stopTicker();
      set({ pops: {} });
    },
    sweepExpired: () => {
      const cutoff = Date.now() - POP_TTL_MS;
      set((state) => {
        const next = Object.fromEntries(
          Object.entries(state.pops).filter(
            ([, pop]) => pop.receivedAt > cutoff,
          ),
        );
        // Preserve the reference when nothing expired so subscribers to
        // `pops` don't re-render on every tick.
        if (Object.keys(next).length === Object.keys(state.pops).length) {
          return state;
        }
        return { pops: next };
      });
      if (Object.keys(get().pops).length === 0) stopTicker();
    },
  }),
);
